/**
 * DocumentUploader Component
 * Drag-and-drop file upload interface for knowledge documents
 */

import React, { useRef, useState } from 'react';
import { FiUpload, FiFile, FiLoader } from 'react-icons/fi';

interface DocumentUploaderProps {
  onUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  disabled?: boolean;
  maxSize?: number; // in MB
  acceptedTypes?: string[];
  className?: string;
}

const DEFAULT_ACCEPTED_TYPES = [
  '.pdf',
  '.txt',
  '.doc',
  '.docx',
  '.md',
  '.csv',
  '.json'
];

export const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  onUpload,
  isUploading,
  disabled = false,
  maxSize = 10, // 10MB default
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  className = ''
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = (file: File): string | null => {
    // Check file size
    const maxBytes = maxSize * 1024 * 1024;
    if (file.size > maxBytes) {
      return `File size exceeds ${maxSize}MB limit`;
    }

    // Check file type
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedTypes.includes(extension)) {
      return `File type ${extension} not supported. Accepted: ${acceptedTypes.join(', ')}`;
    }

    return null;
  };

  const handleFile = async (file: File) => {
    setError(null);

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await onUpload(file);
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    // Reset input
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled || isUploading) return;

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !isUploading) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  return (
    <div className={className}>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          bg-slate-800/50 backdrop-blur-sm rounded-xl border-2 border-dashed transition-all
          ${isDragging
            ? 'border-blue-500 bg-blue-500/10'
            : error
            ? 'border-red-500/50'
            : 'border-slate-700'
          }
          ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <div
          onClick={() => !disabled && !isUploading && fileInputRef.current?.click()}
          className="p-8 text-center"
        >
          {isUploading ? (
            <div className="space-y-3">
              <FiLoader className="mx-auto animate-spin text-blue-400" size={48} />
              <p className="text-gray-300 font-medium">Uploading document...</p>
              <p className="text-sm text-gray-500">Processing and indexing content</p>
            </div>
          ) : (
            <>
              <FiUpload className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-white mb-2">
                Upload Knowledge Document
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                Drag and drop a file here, or click to browse
              </p>
              <div className="flex flex-wrap justify-center gap-2 text-xs text-gray-500">
                {acceptedTypes.map((type) => (
                  <span key={type} className="px-2 py-1 bg-slate-700 rounded">
                    {type}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Maximum file size: {maxSize}MB
              </p>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes.join(',')}
          onChange={handleFileSelect}
          disabled={disabled || isUploading}
          className="hidden"
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-3 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Disabled Message */}
      {disabled && !isUploading && (
        <div className="mt-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
          <p className="text-sm text-yellow-400">
            Please select or create an assistant first
          </p>
        </div>
      )}
    </div>
  );
};

export default DocumentUploader;
